import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, lighten, Typography, useTheme } from '@mui/material';
import {
    BoxSkeleton,
    ConfirmationModal,
    SkeletonLoader,
    useModal,
} from '@europrocurement/flexy-components';
import {
    type AddArticleParamsType,
    ArticleCategory,
    CATEGORY_BODACC,
    CATEGORY_GREFFE,
    CATEGORY_LEGAL_ANNOUCEMENT,
    CATEGORY_TAXES,
    type DossierArticle,
    type DossierArticleWithInvoice,
    REFRESH_CURRENT_DOSSIER_CTX,
    useCurrentDossierCtx,
    useCurrentDossierCtxDispatch,
    useDossierArticle,
    useDossierArticleContext,
    useDossierProcesses,
    useDossierProcessesActionsAndRender,
    useDossierRoutingContext,
    useDossierValidatorsCtx,
    VALIDATOR_TYPE_GREFFE,
} from '@europrocurement/l2d-modules';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { enqueueSnackbar } from 'notistack';
import { useNavigate } from 'react-router';
import DossierFormaliteProcesses from '../DossierFormaliteProcesses/DossierFormaliteProcesses';
import DossierLegalAnnoucements from '../DossierLegalAnnoucements/DossierLegalAnnoucements';
import DossierBodaccProcesses from '../DossierBodaccProcesses/DossierBodaccProcesses';
import DossierImpotProcesses from '../DossierImpotProcesses/DossierImpotProcesses';
import DossierArticlesProcesses from '../DossierArticlesProcesses/DossierArticlesProcesses';
import DossierProcessesFooter from '../DossierProcessesFooter/DossierProcessesFooter';
import AddProcessForm from '../AddProcessForm/AddProcessForm';
import { AddTaxFormData } from '../AddTaxForm/AddTaxForm';
import UpdateValidatorForm from '../UpdateValidatorForm/UpdateValidatorForm';
import { DossierArticleProcessMenuItemProps } from '../DossierArticleProcessMenu/DossierArticleProcessMenu';

const DossierProcesses: React.FunctionComponent = function () {
    const { dossier } = useCurrentDossierCtx();
    const dispatch = useCurrentDossierCtxDispatch();

    const refreshDossier = useCallback(() => {
        if (dispatch) {
            dispatch({ type: REFRESH_CURRENT_DOSSIER_CTX });
        }
    }, [dispatch]);

    const { addArticle, deleteArticle, getArticlesWithInvoicesByDossier } = useDossierArticle();
    const {
        createValidatorProcess,
        canCreateValidatorProcess,
        findProcessByArticle,
        getArticleProcessesStatus,
        getArticleProcessesStatusLabel,
        getArticleProcessesStatusColor,
        canAddFormality,
    } = useDossierProcesses();
    const { actionUpdateValidatorStatus } = useDossierProcessesActionsAndRender();
    const { getAddFormalityRoute } = useDossierRoutingContext();
    const { palette } = useTheme();
    const { modalActions } = useModal();
    const { articleCategories } = useDossierArticleContext();
    const { getDossierValidatorById } = useDossierValidatorsCtx();
    const navigate = useNavigate();

    const [articlesLoaded, setArticlesLoaded] = useState<boolean>(false);
    const [articles, setArticles] = useState<Array<DossierArticleWithInvoice>>([]);
    const [blockDisplayed, setBlockDisplayed] = useState<Array<number>>([]);

    const refreshArticles = async (dossierId: number) => {
        setArticlesLoaded(false);

        try {
            const response = await getArticlesWithInvoicesByDossier(dossierId);

            setArticles(response);
        } catch (e) {
            console.error(e);
        } finally {
            setBlockDisplayed([]);
            setArticlesLoaded(true);
        }
    };

    useEffect(() => {
        if (dossier?.id) {
            refreshArticles(dossier.id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dossier?.id, dossier?.validationProcesses]);

    const findArticlesByCategory = (categoryId: number): Array<DossierArticleWithInvoice> => {
        if (!articlesLoaded) {
            return [];
        }

        return articles.filter((article) => article.categoryId === categoryId);
    };

    const findBodaccArticles = (): Array<DossierArticleWithInvoice> => {
        const bodaccArticles = findArticlesByCategory(CATEGORY_BODACC);

        return bodaccArticles.filter((res) => res.article.rubArticle === 'FRN');
    };

    const onSubmitProcess = async (formData: AddTaxFormData) => {
        if (!dossier) return;
        try {
            const articleId = await addArticle({
                prestationId: formData.prestationId,
                label: formData.title,
                factArticleId: formData.articleId,
            } as AddArticleParamsType);

            const process = await createValidatorProcess(dossier.id as number, [articleId]);
            if (process) {
                refreshDossier();
            }
            //  refreshDossier();
        } catch (e) {
            console.error(e);
            enqueueSnackbar("Une erreur est survenue lors de l'ajout de votre démarche", {
                variant: 'error',
            });
        } finally {
            modalActions.close();
        }
    };

    const onRemoveProcess = async (articleId: string) => {
        try {
            await deleteArticle(articleId);
            // refreshDossier();
        } catch (e) {
            console.error(e);
            enqueueSnackbar('Une erreur est survenue lros de la suppression de votre démarche', {
                variant: 'error',
            });
        } finally {
            modalActions.close();
        }
    };

    const getItemActions = (
        article: DossierArticle,
        validatorTypeId: number,
    ): Array<DossierArticleProcessMenuItemProps> => {
        if (!dossier) return [];
        const actions = [];
        const process = findProcessByArticle(dossier, [validatorTypeId], article.id);
        const statuses = process
            ? getDossierValidatorById(process.validatorId)?.statuses || []
            : [];

        if (process) {
            actions.push(
                actionUpdateValidatorStatus(() => {
                    modalActions.call(
                        <UpdateValidatorForm
                            processing={false}
                            process={process}
                            onItemSelected={() => {
                                modalActions.close();
                                refreshDossier();
                            }}
                            statuses={statuses}
                        />,
                    );
                }),
            );
        }

        actions.push({
            label: 'Supprimer la démarche',
            icon: 'trash',
            onClick: (currentArticle: DossierArticle) => {
                modalActions.call(
                    <ConfirmationModal
                        color={palette.error}
                        messages={{
                            question: 'Confirmer cette action ?',
                            detail: {
                                message: 'Êtes vous sur de vouloir supprimer la démarche ?',
                                severity: 'error',
                            },
                            validation: 'Supprimer',
                        }}
                        actionOnValidation={() => onRemoveProcess(currentArticle.id.toString())}
                        actionOnCancellation={() => modalActions.close()}
                    />,
                );
            },
        });

        return actions as Array<DossierArticleProcessMenuItemProps>;
    };

    const getMinSortOrderInCategory = (category: ArticleCategory): number => {
        const articlesFromCategory =
            category.id === CATEGORY_BODACC
                ? findBodaccArticles()
                : findArticlesByCategory(category.id);

        return articlesFromCategory.reduce(
            (minRang, current) => (current.rang < minRang ? current.rang : minRang),
            999,
        );
    };

    const blocks = useMemo(
        () =>
            articleCategories
                .sort((categoryA, categoryB) => {
                    const minArticlesByCategoryA = getMinSortOrderInCategory(categoryA);
                    const minArticlesByCategoryB = getMinSortOrderInCategory(categoryB);

                    return minArticlesByCategoryA > minArticlesByCategoryB ? 1 : -1;
                })
                .filter((articleCategory) => articleCategory.validatorTypeId)
                .map((articleCategory) => {
                    let checked = blockDisplayed.includes(articleCategory.id);

                    if (articleCategory.id === CATEGORY_GREFFE) {
                        checked = !!(
                            checked ||
                            (dossier &&
                                dossier.validationProcesses &&
                                dossier.validationProcesses.filter(
                                    (process) => process.validatorTypeId === VALIDATOR_TYPE_GREFFE,
                                ).length > 0)
                        );
                    } else if (articleCategory.id === CATEGORY_BODACC) {
                        checked = checked || findBodaccArticles().length > 0;
                    } else {
                        checked = checked || findArticlesByCategory(articleCategory.id).length > 0;
                    }

                    return {
                        id: articleCategory.id,
                        label: articleCategory.label,
                        validatorTypeId: articleCategory.validatorTypeId,
                        checked,
                    };
                }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [articleCategories, blockDisplayed, findBodaccArticles, findArticlesByCategory],
    );

    const isCategoryVisible = (idCategory: number) => {
        const category = blocks.find((c) => c.id === idCategory);

        return category?.checked || false;
    };

    const showAddProcessModal = (categoryId: number) => {
        if (!dossier) return;
        modalActions.call(
            <AddProcessForm
                categoryId={categoryId}
                dossier={dossier}
                onSubmit={onSubmitProcess}
                onCancel={() => modalActions.close()}
            />,
        );
    };

    const navigateToParcours = () => {
        const path = getAddFormalityRoute(dossier?.id as number);

        if (path) {
            navigate(path);
        }
    };

    if (!dossier) {
        return null;
    }

    return (
        <SkeletonLoader
            isLoading={!articlesLoaded}
            containerSx={{
                marginTop: '10px',
                marginBottom: '10px',
            }}
            skeleton={
                <BoxSkeleton
                    sx={{
                        height: '250px',
                    }}
                />
            }
        >
            <Box
                sx={{
                    padding: '0 0 16px 0',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px 0',
                }}
            >
                <Box
                    sx={{
                        padding: '10px 0 0 0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Typography
                        variant="l2dsubtitles"
                        data-testid="title-dossier-process"
                    >
                        Démarches
                    </Typography>
                    {canAddFormality(dossier) && (
                        <Button
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0 8px',
                                color: lighten(palette.secondary.main, 0.3),
                            }}
                            variant="text"
                            onClick={navigateToParcours}
                        >
                            <FaOptionIcon
                                icon={['fas', 'plus']}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    lineHeight: 0,
                                }}
                            />
                            <Typography variant="l2dcaption">Ajouter une formalité</Typography>
                        </Button>
                    )}
                </Box>

                {blocks
                    .filter((category) => isCategoryVisible(category.id))
                    .map((category) => {
                        if (category.id === CATEGORY_GREFFE) {
                            return <DossierFormaliteProcesses key={category.label} />;
                        }

                        if (category.id === CATEGORY_LEGAL_ANNOUCEMENT) {
                            return (
                                <DossierLegalAnnoucements
                                    key={category.label}
                                    articles={findArticlesByCategory(category.id)}
                                />
                            );
                        }

                        if (category.id === CATEGORY_BODACC) {
                            return (
                                <DossierBodaccProcesses
                                    key={category.label}
                                    articles={findBodaccArticles()}
                                />
                            );
                        }

                        if (category.id === CATEGORY_TAXES) {
                            return (
                                <DossierImpotProcesses
                                    key={category.label}
                                    articles={findArticlesByCategory(category.id)}
                                />
                            );
                        }

                        const footer = canCreateValidatorProcess(
                            dossier,
                            category.validatorTypeId,
                        ) ? (
                            <DossierProcessesFooter
                                key={category.label}
                                handleAddItem={() => showAddProcessModal(category.id)}
                                addItemTextBtn="Ajouter un enregistrement"
                                canAddItem
                            />
                        ) : null;

                        const processArticles = findArticlesByCategory(category.id);
                        const status = getArticleProcessesStatus(
                            dossier,
                            category.validatorTypeId,
                            processArticles.map((a) => a.id),
                        );

                        return (
                            <DossierArticlesProcesses
                                key={category.label}
                                title={category.label}
                                validatorTypeId={category.validatorTypeId}
                                footer={footer}
                                getItemActions={(article: DossierArticle) =>
                                    getItemActions(article, category.validatorTypeId)
                                }
                                articles={processArticles}
                                statusLabel={getArticleProcessesStatusLabel(status)}
                                statusColor={getArticleProcessesStatusColor(status)}
                            />
                        );
                    })}
            </Box>
        </SkeletonLoader>
    );
};

export default DossierProcesses;
